import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import {
  useWaitingTimeServiceV1GetWaitingTimeRateQuery,
  useWaitingTimeServiceV1UpdateWaitingTimeRateMutation,
  type WaitingTimeServiceV1UpdateWaitingTimeRateBody,
} from "_services/modules/waiting_time";
import { DurationCondition } from "_services/modules/waitingTimeRate";
import { yupNumTransform } from "_helper/formatters";
import { toast } from "react-toastify";
import { errorHandler } from "_services/errorHandler";

export interface EditWaitingTimeRateFormValues {
  waitingTimeRateName: string;
  durationCondition: DurationCondition;
  durationValue: number | string;
  durationMin: number | string;
  durationMax: number | string;
  rate: number | string;
  description: string;
}

const schema = yup.object({
  waitingTimeRateName: yup.string().required("Waiting Time Rate Name is required").max(100, "Max 100 characters"),
  durationCondition: yup.string().oneOf(["below", "between", "above"]).required("Duration Condition is required"),
  durationValue: yup.mixed().when("durationCondition", {
    is: (val: string) => val === "below" || val === "above",
    then: () => yup.number().transform(yupNumTransform).typeError("Must be a number").required("Duration value is required").positive("Must be positive"),
    otherwise: () => yup.mixed().notRequired(),
  }),
  durationMin: yup.mixed().when("durationCondition", {
    is: "between",
    then: () => yup.number().transform(yupNumTransform).typeError("Must be a number").required("Min value is required").min(0, "Must be >= 0"),
    otherwise: () => yup.mixed().notRequired(),
  }),
  durationMax: yup.mixed().when("durationCondition", {
    is: "between",
    then: () => yup.number().transform(yupNumTransform).typeError("Must be a number").required("Max value is required").positive("Must be positive")
      .when("durationMin", (durationMin, schema) => {
        const minVal = Array.isArray(durationMin) ? durationMin[0] : durationMin;
        if (typeof minVal === "number" && !isNaN(minVal)) {
          return schema.moreThan(minVal, "Max must be greater than Min");
        }
        return schema;
      }),
    otherwise: () => yup.mixed().notRequired(),
  }),
  rate: yup.number().transform((value, originalValue) => {
    if (typeof originalValue === "string" && originalValue !== "") {
      const normalized = originalValue.replace(/[^0-9,\-]/g, "").replace(",", ".");
      return Number(normalized);
    }
    return value;
  }).typeError("Must be a number").required("Rate is required").positive("Must be positive"),
  description: yup.string().max(500, "Max 500 characters").default(""),
}).required();

const useEditWaitingTimeRateForm = (id: string | undefined) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("waitingTimeRate");

  const { data: detail } = useWaitingTimeServiceV1GetWaitingTimeRateQuery(
    { id: id || "" },
    { skip: !id }
  );
  const [updateWaitingTimeRate] = useWaitingTimeServiceV1UpdateWaitingTimeRateMutation();

  const {
    handleSubmit,
    formState: { errors },
    register,
    control,
    watch,
    setValue,
    setError,
  } = useForm<EditWaitingTimeRateFormValues>({
    mode: "onBlur",
    resolver: yupResolver(schema) as never,
    defaultValues: {
      // TODO: load from API using GET /waiting-time-rates/{id}
      waitingTimeRateName: "Short Wait",
      durationCondition: "below",
      durationValue: 2,
      durationMin: "",
      durationMax: "",
      rate: 75000,
      description: "",
    },
  });

  const durationCondition = watch("durationCondition");

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [pendingData, setPendingData] = useState<EditWaitingTimeRateFormValues | null>(null);

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-edit"),
        hasBackButton: true,
      })
    );
  }, [dispatch, t]);

  useEffect(() => {
    if (detail) {
      const data = detail;
      setValue("waitingTimeRateName", data.waiting_time_rate_name || "");
      setValue("durationCondition", (data.duration_condition as any) || "below");
      setValue("rate", data.rate || "");
      
      if (data.duration_condition === "between") {
        setValue("durationMin", data.duration_min ?? "");
        setValue("durationMax", data.duration_max ?? "");
      } else if (data.duration_condition === "below") {
        setValue("durationValue", data.duration_max ?? "");
      } else if (data.duration_condition === "above") {
        setValue("durationValue", data.duration_min ?? "");
      }
    }
  }, [detail, setValue]);

  const onSubmit = (data: EditWaitingTimeRateFormValues) => {
    setPendingData(data);
    setShowSaveModal(true);
  };

  const handleConfirmSave = async () => {
    if (!pendingData || !id) return;
    try {
      const payload: Omit<WaitingTimeServiceV1UpdateWaitingTimeRateBody, 'status'> & { status?: string } = {
        waiting_time_rate_name: pendingData.waitingTimeRateName,
        duration_condition: pendingData.durationCondition,
        duration_min: pendingData.durationCondition === "between"
          ? Number(pendingData.durationMin)
          : (pendingData.durationCondition === "above" ? Number(pendingData.durationValue) : undefined),
        duration_max: pendingData.durationCondition === "between"
          ? Number(pendingData.durationMax)
          : (pendingData.durationCondition === "below" ? Number(pendingData.durationValue) : undefined),
        rate: Number(pendingData.rate),
      };
      await updateWaitingTimeRate({ id, waitingTimeServiceV1UpdateWaitingTimeRateBody: payload }).unwrap();
      toast.success(t("toast-update-success"));
      setShowSaveModal(false);
      navigate(-1);
    } catch (error: any) {
      if (error?.status === 409) {
        setError("waitingTimeRateName", {
          type: "validate",
          message: t("error-duplicate-exists"),
        });
      } else if (error?.status === 404) {
        setShowSaveModal(false);
        navigate(-1);
      } else {
        errorHandler(error);
      }
      setShowSaveModal(false);
    }
  };

  const handleConfirmCancel = () => {
    setShowCancelModal(false);
    navigate(-1);
  };

  return {
    handleSubmit,
    errors,
    register,
    control,
    durationCondition,
    setError,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    onSubmit,
    handleConfirmSave,
    handleConfirmCancel,
    t,
  };
};

export default useEditWaitingTimeRateForm;
